import React, { Component, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';

import { compact, get } from 'lodash-es';
import { AdSlot, DFPManager } from '@edmunds/react-dfp';
import {
  handleAdLoad,
  fireHeavyAdInterventionEvent,
} from 'client/engagement-handlers/ads-engagement-handler/ads-engagement-handler';
import { RefreshRules } from 'client/site-modules/shared/components/ad-unit/utils/refresh-rules';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { isRenderedAdUnit } from 'client/utils/ads';
import { pushToBidEligibleAdUnits } from 'client/utils/pwt/get-bid-eligible-array';
import { getRenderWhenViewablePwtBids } from 'client/utils/pwt/get-pwt-bids';

import { GPT_NETWORK_CODE } from './context/sitename';
import { AdUnitPropTypes, AdUnitDefaultProps } from './ad-unit-prop-types';
import {
  getAdSizes,
  getTargeting,
  getSitename,
  getClassNames,
  refreshAdUnit,
  isPwtWhitelistAd,
} from './ad-unit-helper';
import { AdsPositionManager } from './ads-position-manager';

import './ad-unit.scss';

export class AdUnitSlot extends Component {
  constructor(props) {
    super(props);

    const { adName, nativeStyle, position } = props;

    this.ref = createRef();

    this.state = {
      isRenderPlaceholderComponent: false,
      isShowLabel: this.props.showLabel,
      lineItemId: '',
      adName: '',
      slotId: '',
    };

    this.adZonePosition = AdsPositionManager.getInstance().getPosition(adName, nativeStyle, position);
  }

  async componentDidMount() {
    const { pwtEnabled, adName, renderWhenViewable } = this.props;

    // IMPORTANT: PWT can not work correctly on pages with continues scroll: position value can be changed
    if (pwtEnabled && isPwtWhitelistAd(adName)) {
      const sizes = getAdSizes(this.props).filter(size => Array.isArray(size) && size[0] > 2);
      const slotId = this.getSlotId();
      const adUnitObj = {
        id: `${adName}_${this.props.position}`,
        dfpNetworkId: GPT_NETWORK_CODE,
        slotId,
        adUnitId: `/${GPT_NETWORK_CODE}/${getSitename(this.props)}`,
        adUnitIndex: `${this.props.position}`,
        sizes,
      };
      if (renderWhenViewable && window.EDM.Venom.adsLoadTriggered) {
        const adUnitObjWithDivId = { ...adUnitObj, divId: slotId };
        getRenderWhenViewablePwtBids([adUnitObjWithDivId]);
      } else {
        pushToBidEligibleAdUnits(adUnitObj);
      }
    }
    window.addEventListener('message', this.messageListener, false);
  }

  componentDidUpdate(prevProps) {
    const { vehicleContextChange, visitorLocation, adName, refreshDisableWhenZipChanged } = this.props;
    const prevZipCode = prevProps.visitorLocation.zipCode;
    const zipCode = visitorLocation.zipCode;

    if (!refreshDisableWhenZipChanged && !vehicleContextChange && prevZipCode !== zipCode) {
      // When zip code is changed, ads should be refreshed. The refresh action is suppressed for site-served ads.
      refreshAdUnit(adName, this.getSlotId());
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.messageListener, false);
  }

  onSlotRegister = event => {
    const { onSlotRegister, pwtEnabled, adName, skipPwtRefresh, renderWhenViewable } = this.props;
    if (onSlotRegister) {
      onSlotRegister(event);
    }
    const slotId = this.getSlotId();
    if (window.EDM.Venom.privacyChanged) {
      // enableGpc FF is enabled
      setTimeout(() => {
        const dfpSlot = window.googletag
          .pubads()
          .getSlots()
          .find(slot => slot.getSlotElementId() === slotId);
        window.googletag.cmd.push(() => {
          window.googletag.pubads().refresh([dfpSlot]);
        });
      });
    } else if (
      pwtEnabled &&
      window?.PWT?.adsPwtLoaded &&
      (!renderWhenViewable || (renderWhenViewable && adName.includes('companion')))
    ) {
      refreshAdUnit(adName, slotId, skipPwtRefresh);
    }
  };

  onSlotRender = ({ event }) => {
    const { adName, placeholderComponent, showLabel, slotRenderEndListener, refreshable } = this.props;
    const eventData = {
      ...event,
      slotId: this.getSlotId(),
      adName,
    };

    this.setState({
      lineItemId: eventData.lineItemId,
      isRenderPlaceholderComponent: placeholderComponent && !isRenderedAdUnit(eventData),
      isShowLabel: showLabel && isRenderedAdUnit(eventData),
    });

    if (slotRenderEndListener && typeof slotRenderEndListener === 'function') {
      slotRenderEndListener(eventData);
    }

    if (refreshable) {
      RefreshRules.addToRefreshableUnitsArray(this.getSlotId(), this.props);
    }

    handleAdLoad(eventData);

    if (!isRenderedAdUnit(event)) {
      this.ref.current.querySelector('iframe')?.setAttribute('tabindex', '-1');
    }
  };

  onSlotVisibilityChanged = ({ event }) => {
    const { inViewPercentage } = event;
    if (this.props.refreshWhenViewable) {
      if (inViewPercentage === 100 && this.previousVisability !== 0) {
        if (this.wasShowedOnce && this.wasHidden) {
          DFPManager.refresh(this.getSlotId());
          this.wasHidden = false;
        }
        this.wasShowedOnce = true;
      } else if (inViewPercentage === 0 && this.previousVisability !== 100) {
        this.wasHidden = true;
      }
      this.previousVisability = inViewPercentage;
    }
  };

  getPosition = () => {
    const { hasAdZone, isIncrementPosition, position } = this.props;
    return hasAdZone && isIncrementPosition ? this.adZonePosition : position;
  };

  getSlotId = () => {
    const { prefixId, adName, nativeStyle, page, customTargeting } = this.props;
    return compact([prefixId, adName, nativeStyle, `${this.getPosition()}`, page.name, get(customTargeting, 'sect8')])
      .join('-')
      .replace(',', '_');
  };

  messageListener = ({ data }) => {
    const { lineItemId } = this.state;
    const messageId = get(data, 'body.id', '');
    const messageLineItemId = get(data, 'lineItemId', '');
    const messageText = get(data, 'body.message', '');

    if (messageId === 'HeavyAdIntervention' && lineItemId) {
      fireHeavyAdInterventionEvent(messageLineItemId, messageText);
    }
  };

  render() {
    const { adName, nativeStyle, placeholderComponent: PlaceholderComponent, placeholderComponentProps } = this.props;
    const { isRenderPlaceholderComponent } = this.state;

    const containerProps = {
      className: getClassNames(this.props, this.state),
      'data-adname': adName,
      'data-native-style': nativeStyle,
    };

    return (
      <Fragment>
        <div {...containerProps} ref={this.ref}>
          <ClientSideRender showTimeout={1}>
            <AdSlot
              dfpNetworkId={GPT_NETWORK_CODE}
              slotId={this.getSlotId()}
              adUnit={getSitename(this.props)}
              sizes={getAdSizes(this.props)}
              targetingArguments={getTargeting({
                ...this.props,
                position: this.getPosition(),
              })}
              onSlotRender={this.onSlotRender}
              onSlotVisibilityChanged={this.onSlotVisibilityChanged}
              onSlotRegister={this.onSlotRegister}
            />
          </ClientSideRender>
        </div>
        {isRenderPlaceholderComponent && <PlaceholderComponent {...placeholderComponentProps} />}
        {/* todo shouldn't placeholder be rendered on both server and client to avoid flickers */}
      </Fragment>
    );
  }
}

AdUnitSlot.propTypes = {
  ...AdUnitPropTypes,
  prefixId: PropTypes.string,
  hasAdZone: PropTypes.bool,
  isIncrementPosition: PropTypes.bool,
};

AdUnitSlot.defaultProps = {
  ...AdUnitDefaultProps,
  prefixId: undefined,
  hasAdZone: false,
  isIncrementPosition: false,
};
